import { testMobileNumber } from '@getaccept/lib-shared/src/helpers';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type {
  InviteRecipientData,
  SigningPageRecipient,
} from '@getaccept/lib-shared/src/types/signing-page-recipient';
import { useValidationSchema } from '@getaccept/lib-shared/src/validation';
import type { MaybeRef } from 'vue';
import { computed, unref } from 'vue';
import { object, string } from 'yup';

export const useValidateRecipient = (
  data: MaybeRef<InviteRecipientData>,
  recipients: MaybeRef<SigningPageRecipient[]>
) => {
  const {
    result: exists,
    validate: checkDuplicate,
    validateAll: checkAllDuplicates,
  } = useValidationSchema(
    data,
    object({
      mobile: string().test(
        'unique',
        t('phone_already_added'),
        value => !hasValue(value, 'mobile')
      ),
      email: string().test('unique', t('email_already_added'), value => !hasValue(value, 'email')),
    }),
    { validateOnUpdate: false, debounce: 500 }
  );

  const hasValue = (value: string, key: keyof InviteRecipientData) =>
    !!value && unref(recipients).some(recipient => recipient[key] === value);

  const matchedExistingRecipient = computed(() =>
    unref(recipients).find(
      recipient =>
        recipient.email === unref(data).email ||
        (!!recipient.mobile && recipient.mobile === unref(data).mobile)
    )
  );

  const isRecipientAlreadyAdded = computed(() => !!matchedExistingRecipient.value);

  const warningTitle = computed(
    () => exists.value.email.errorMessage || exists.value.mobile.errorMessage
  );

  const warningDescription = computed(() => {
    if (!isRecipientAlreadyAdded.value) return '';
    return exists.value.email.errorMessage
      ? t('email_already_exists_description')
      : t('mobile_already_exists_description');
  });

  const { result, ...validator } = useValidationSchema(
    data,
    object({
      firstName: string().required(t('first-name-is-required')),
      lastName: string().required(t('last-name-is-required')),
      companyName: string().required(t('company_name_is_required')),
      title: string().required(t('job_title_is_required')),
      email: string().email(t('enter-a-valid-email')).required(t('email-is-required')),
      mobile: string()
        .test('valid', t('enter-a-valid-phone-number'), value => !testMobileNumber(value))
        .test('required', t('phone-number-is-required'), value => !!unref(data).email || !!value),
    }),
    { validateSuccessOnUpdate: true, debounce: 500 }
  );

  const emailError = computed(
    () => result.value.email.errorMessage || exists.value.email.errorMessage
  );

  const mobileError = computed(
    () => result.value.mobile.errorMessage || exists.value.mobile.errorMessage
  );

  return {
    checkDuplicate,
    checkAllDuplicates,
    isRecipientAlreadyAdded,
    warningTitle,
    warningDescription,
    matchedExistingRecipient,
    result,
    emailError,
    mobileError,
    ...validator,
  };
};
